/**
 * WebSocket Context
 * 
 * Provides WebSocket client instance to React components via Context API.
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { WebSocketClientManager, createDefaultConfig } from './WebSocketClientManager';
import { ConnectionStatus } from './types';
import { wsLogger } from './logger';
import { useUserSubscriptions } from '../../hooks/useUserSubscriptions';

/**
 * WebSocket Context type
 */
interface WebSocketContextType {
  client: WebSocketClientManager | null;
  status: ConnectionStatus;
  isConnected: boolean;
  isFallbackMode: boolean;
  reconnectAttempt: number;
  connect: () => Promise<void>;
  disconnect: () => void;
  userSubscriptionsReady: boolean;
}

/**
 * WebSocket Context
 */
const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

/**
 * WebSocket Provider Props
 */
interface WebSocketProviderProps {
  children: React.ReactNode;
  authToken: string;
  autoConnect?: boolean;
}

/**
 * 内部组件：在 WebSocketContext 内部调用 useUserSubscriptions
 * 这样可以消费 WebSocketContext，同时将 isReady 状态提升到 Provider
 */
const UserSubscriptionsInitializer: React.FC<{
  onReady: (ready: boolean) => void;
  children: React.ReactNode;
}> = ({ onReady, children }) => {
  const { isReady } = useUserSubscriptions();

  useEffect(() => {
    onReady(isReady);
  }, [isReady, onReady]);

  return <>{children}</>;
};

/**
 * WebSocket Provider Component
 */
export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({
  children,
  authToken,
  autoConnect = true
}) => {
  const [client, setClient] = useState<WebSocketClientManager | null>(null);
  const [status, setStatus] = useState<ConnectionStatus>(ConnectionStatus.DISCONNECTED);
  // isConnected 作为独立 state，只在 connect/disconnect 事件时更新，避免每秒轮询导致的重渲染
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [reconnectAttempt, setReconnectAttempt] = useState<number>(0);
  const [userSubscriptionsReady, setUserSubscriptionsReady] = useState<boolean>(false);

  // 用 useCallback 稳定化 onReady，避免 UserSubscriptionsInitializer 因 prop 引用变化而重复触发 useEffect
  const handleSubscriptionsReady = useCallback((ready: boolean) => {
    setUserSubscriptionsReady(ready);
  }, []);

  // Initialize client
  useEffect(() => {
    wsLogger.info('Context', 'Provider initializing...', {
      hasToken: !!authToken,
      tokenLength: authToken?.length || 0,
      autoConnect
    });

    if (!authToken) {
      wsLogger.warn('Context', 'No auth token provided');
      return;
    }

    const config = createDefaultConfig(authToken);
    const wsClient = new WebSocketClientManager(config);

    // Setup connection event handlers
    // 只在真实事件时更新 state，不做轮询，避免不必要的重渲染
    wsClient.setConnectionEventHandlers({
      onConnect: () => {
        wsLogger.info('Context', 'Event: Connected');
        setStatus(ConnectionStatus.CONNECTED);
        setIsConnected(true);
        setReconnectAttempt(0);
      },
      onDisconnect: (reason) => {
        wsLogger.info('Context', `Event: Disconnected - ${reason}`);
        setStatus(ConnectionStatus.DISCONNECTED);
        setIsConnected(false);
      },
      onReconnect: (attemptNumber) => {
        wsLogger.info('Context', `Event: Reconnected after ${attemptNumber} attempts`);
        setStatus(ConnectionStatus.CONNECTED);
        setIsConnected(true);
        setReconnectAttempt(0);
      },
      onReconnectFailed: () => {
        wsLogger.error('Context', 'Event: Reconnection failed');
        setStatus(ConnectionStatus.FAILED);
        setIsConnected(false);
      },
      onError: (error) => {
        wsLogger.error('Context', `Event: Error - ${error.message}`);
      }
    });

    setClient(wsClient);

    // Auto-connect if enabled
    if (autoConnect) {
      wsLogger.info('Context', 'Auto-connecting...');
      wsClient.connect().catch(error => {
        wsLogger.error('Context', 'Auto-connect failed', { error });
      });
    }

    // Cleanup on unmount
    return () => {
      wsLogger.info('Context', 'Provider cleanup');
      wsClient.disconnect();
    };
  }, [authToken, autoConnect]);

  // 移除每秒轮询的 setInterval，改为只在重连尝试时更新 reconnectAttempt
  // reconnectAttempt 通过 onReconnect 事件更新，不需要轮询

  // Connect function
  const connect = useCallback(async () => {
    if (!client) {
      throw new Error('WebSocket client not initialized');
    }
    await client.connect();
  }, [client]);

  // Disconnect function
  const disconnect = useCallback(() => {
    if (!client) {
      throw new Error('WebSocket client not initialized');
    }
    client.disconnect();
  }, [client]);

  // 用 useMemo 稳定化 value，只有真正变化的字段才触发重渲染
  const value = useMemo<WebSocketContextType>(() => ({
    client,
    status,
    isConnected,
    isFallbackMode: client?.isFallback() ?? false,
    reconnectAttempt,
    connect,
    disconnect,
    userSubscriptionsReady
  }), [client, status, isConnected, reconnectAttempt, connect, disconnect, userSubscriptionsReady]);

  return (
    <WebSocketContext.Provider value={value}>
      <UserSubscriptionsInitializer onReady={handleSubscriptionsReady}>
        {children}
      </UserSubscriptionsInitializer>
    </WebSocketContext.Provider>
  );
};

/**
 * Hook to use WebSocket context
 */
export const useWebSocketContext = (): WebSocketContextType => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocketContext must be used within WebSocketProvider');
  }
  return context;
};
